import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IOrganizationSettings } from 'api/portalUser/types';
import { THEME_VARIANT } from 'constants/defaultPalette';
import { getTransactionReportingPeriod } from 'queries/session/utils';

export const utcTimeZone = 'Etc/UTC';
const { endDate, startDate } = getTransactionReportingPeriod(utcTimeZone);

export enum FILTER_OPERATIONS_SORT_STRATEGIES {
  EQUALS_FIRST = 'EQUALS_FIRST',
  LIKES_FIRST = 'LIKES_FIRST',
}

export enum LANGUAGES {
  EN = 'EN',
  UK = 'UK',
  VI = 'VI',
}

export interface IUserState {
  filterOperationsSortStrategy: FILTER_OPERATIONS_SORT_STRATEGIES;
  isGridColumnSettingExpanded: boolean;
  isLeaderTab: boolean;
  isNewReport?: Record<string, boolean>;
  isOpenBar: boolean;
  language: LANGUAGES;
  orgnSettings?: IOrganizationSettings;
  themeVariant: THEME_VARIANT;
  transactionReportingPeriod: {
    endDate: string;
    startDate: string;
    timeZone: string;
  };
  unauthorizedLanguage: LANGUAGES;
}

export const initialState: IUserState = {
  filterOperationsSortStrategy: FILTER_OPERATIONS_SORT_STRATEGIES.EQUALS_FIRST,
  isGridColumnSettingExpanded: false,
  isLeaderTab: false,
  isOpenBar: !!(window.innerWidth > 1200),
  language: LANGUAGES.EN,
  themeVariant: THEME_VARIANT.DARK,
  transactionReportingPeriod: {
    endDate,
    startDate,
    timeZone: utcTimeZone,
  },
  unauthorizedLanguage: LANGUAGES.EN,
};

export const userConfigurationSlice = createSlice({
  initialState,
  name: 'userConfiguration',
  reducers: {
    resetUserConfiguration: (state: IUserState) => {
      state.language = initialState.language;
      state.themeVariant = initialState.themeVariant;
      state.isOpenBar = initialState.isOpenBar;
      state.isNewReport = initialState.isNewReport;
      state.transactionReportingPeriod = initialState.transactionReportingPeriod;
    },
    setFilterOperationsSortStrategy: (
      state: IUserState,
      action: PayloadAction<{
        filterOperationsSortStrategy: FILTER_OPERATIONS_SORT_STRATEGIES;
      }>,
    ) => {
      state.filterOperationsSortStrategy = action.payload.filterOperationsSortStrategy;
    },
    setIsGridColumnSettingExpanded: (
      state: IUserState,
      action: PayloadAction<{ isExpanded: boolean }>,
    ) => {
      state.isGridColumnSettingExpanded = action.payload.isExpanded;
    },
    setIsLeaderTab: (
      state: IUserState,
      action: PayloadAction<{ isLeaderTab: boolean }>,
    ) => {
      state.isLeaderTab = action.payload.isLeaderTab;
    },
    setLanguage: (state: IUserState, action: PayloadAction<LANGUAGES>) => {
      state.language = action.payload;
    },
    setOrgnSettings: (
      state: IUserState,
      action: PayloadAction<IOrganizationSettings>,
    ) => {
      state.orgnSettings = action.payload;
    },
    setReportDownloadFlag: (
      state: IUserState,
      action: PayloadAction<Record<string, boolean>>,
    ) => {
      state.isNewReport = { ...state.isNewReport, ...action.payload };
    },
    setSidebarState: (state: IUserState, action: PayloadAction<boolean>) => {
      state.isOpenBar = action.payload;
    },
    setTheme: (state: IUserState, action: PayloadAction<THEME_VARIANT>) => {
      state.themeVariant = action.payload;
    },
    setTransactionReportingPeriod: (
      state: IUserState,
      action: PayloadAction<{ endDate: string; startDate: string }>,
    ) => {
      state.transactionReportingPeriod = {
        ...state.transactionReportingPeriod,
        endDate: action.payload.endDate,
        startDate: action.payload.startDate,
      };
    },
    setTransactionReportingTimezone: (
      state: IUserState,
      action: PayloadAction<{ timeZone: string }>,
    ) => {
      state.transactionReportingPeriod = {
        ...state.transactionReportingPeriod,
        timeZone: action.payload.timeZone,
      };
    },
    setUnauthorizedLanguage: (state: IUserState, action: PayloadAction<LANGUAGES>) => {
      state.unauthorizedLanguage = action.payload;
    },
  },
});

export const {
  resetUserConfiguration,
  setFilterOperationsSortStrategy,
  setIsGridColumnSettingExpanded,
  setIsLeaderTab,
  setLanguage,
  setOrgnSettings,
  setReportDownloadFlag,
  setSidebarState,
  setTheme,
  setTransactionReportingPeriod,
  setTransactionReportingTimezone,
  setUnauthorizedLanguage,
} = userConfigurationSlice.actions;
